import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { User, Award, TrendingUp, Trophy, CheckCircle, Star, Target, Zap } from 'lucide-react';
import api from '../services/api';

function Profile() {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get(`/profile/${userId}`);
        setProfile(response.data);
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [userId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <User className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600 text-lg">Profile not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Profile Header */}
      <div style={{marginTop: "73px"}} className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-white rounded-full mb-6 shadow-2xl">
            <User className="w-12 h-12 text-slate-900" />
          </div>
          <h1 className="text-5xl font-bold mb-3">{profile.user?.name}</h1>
          <p className="text-xl text-slate-300 mb-6">
            {profile.user?.bio || 'InternStacks Learner'}
          </p>
          <div className="flex items-center justify-center gap-3">
            <span className="px-4 py-2 bg-blue-500 text-white rounded-full font-semibold flex items-center gap-2">
              <Star className="w-4 h-4" />
              {profile.user?.skillTier}
            </span>
            <span className="px-4 py-2 bg-green-500 text-white rounded-full font-semibold flex items-center gap-2">
              <Trophy className="w-4 h-4" />
              Rank: {profile.user?.rank}
            </span>
          </div>
        </div>
      </div>

      {/* Profile Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Sidebar - Stats & Badges */}
          <div className="space-y-6">
            {/* Stats Card */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
              <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Stats
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-yellow-500" />
                    <span className="text-slate-700 font-medium">Contribution Score</span>
                  </div>
                  <span className="text-2xl font-bold text-slate-900">
                    {profile.user?.contributionScore}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-blue-500" />
                    <span className="text-slate-700 font-medium">Skill Tier</span>
                  </div>
                  <span className="text-lg font-bold text-slate-900">
                    {profile.user?.skillTier}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-green-500" />
                    <span className="text-slate-700 font-medium">Rank</span>
                  </div>
                  <span className="text-lg font-bold text-slate-900">
                    {profile.user?.rank}
                  </span>
                </div>
              </div>
            </div>

            {/* Badges Card */}
            {profile.user?.badges && profile.user.badges.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5" />
                  Badges
                </h3>
                <div className="flex flex-wrap gap-2">
                  {profile.user.badges.map((badge, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-slate-900 rounded-lg text-sm font-semibold shadow-md hover:shadow-lg transition-all"
                    >
                      🏆 {badge}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Main Content - Enrollments */}
          <div className="lg:col-span-2 space-y-6">
            {/* Enrollments Card */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
              <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                <Trophy className="w-6 h-6" />
                Course Enrollments & Certificates
              </h3>
              {profile.enrollments && profile.enrollments.length > 0 ? (
                <div className="space-y-4">
                  {profile.enrollments.map((enrollment) => (
                    <div
                      key={enrollment._id}
                      className="bg-slate-50 border border-slate-200 rounded-lg p-5 hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h4 className="text-lg font-bold text-slate-900 mb-2">
                            {enrollment.program?.title}
                          </h4>
                          <div className="flex items-center gap-4 text-sm">
                            <div className="flex items-center gap-1">
                              <TrendingUp className="w-4 h-4 text-slate-600" />
                              <span className="text-slate-700">
                                Progress: <span className="font-semibold">{enrollment.overallProgress}%</span>
                              </span>
                            </div>
                            {enrollment.certificateIssued ? (
                              <span className="flex items-center gap-1 text-green-700 font-semibold">
                                <CheckCircle className="w-4 h-4" />
                                Certified
                              </span>
                            ) : (
                              <span className="text-slate-600 capitalize">
                                {enrollment.enrollmentStatus}
                              </span>
                            )}
                          </div>
                        </div>
                        {enrollment.certificateIssued && (
                          <button className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-all shadow-md hover:shadow-lg flex items-center gap-2">
                            <Award className="w-4 h-4" />
                            View Certificate
                          </button>
                        )}
                      </div>

                      {/* Progress Bar */}
                      <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-slate-900 to-slate-700 h-full rounded-full transition-all duration-500"
                          style={{ width: `${enrollment.overallProgress}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Trophy className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">No enrollments yet</p>
                </div>
              )}
            </div>

            {/* Achievements Section */}
            {profile.user?.badges && profile.user.badges.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <Award className="w-6 h-6" />
                  Achievements & Badges
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {profile.user.badges.map((badge, idx) => (
                    <div
                      key={idx}
                      className="bg-gradient-to-br from-yellow-400 to-yellow-500 text-slate-900 rounded-lg p-4 text-center font-semibold shadow-md hover:shadow-xl transition-all transform hover:scale-105"
                    >
                      <div className="text-3xl mb-2">🏆</div>
                      <div className="text-sm">{badge}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;
